import { computed, MaybeRef, unref } from 'vue'
import { CustomFieldTypeValueInput, RateType } from '@/gql/graphql'
import { useJobRateCardBandQuery } from '@/components/Job/composables/useJobRateCardBandQuery'
import { useCompanyRateCardBandQuery } from '@/components/Job/composables/useCompanyRateCardBandQuery'

// Direct-hire form only — always sends rate/rateType so the comparator's verdict covers both edges.
// With a job, reads the band stored on the job, not today's card. Without a job (a new brief), or
// when the form changes the job's rate card criteria, rate type or currency, the band comes from the
// company and the form's values, which is what the submit then saves on the job.
export function useHireRateCardBand(options: {
  jobId: MaybeRef<string | null | undefined>
  companyId: MaybeRef<string | null | undefined>
  customFieldValues: MaybeRef<CustomFieldTypeValueInput[]>
  rate: MaybeRef<string | number>
  rateType: MaybeRef<RateType>
  formCriteriaChanged?: MaybeRef<boolean>
  enabled: MaybeRef<boolean>
}) {
  const readsSavedBand = computed(() => !!unref(options.jobId) && !unref(options.formCriteriaChanged))

  const { band: jobBand } = useJobRateCardBandQuery({
    jobId: options.jobId,
    rate: options.rate,
    rateType: options.rateType,
    cached: true,
    enabled: computed(() => unref(options.enabled) && readsSavedBand.value),
  })

  const { band: companyBand } = useCompanyRateCardBandQuery({
    companyId: options.companyId,
    customFieldValues: options.customFieldValues,
    rate: options.rate,
    rateType: options.rateType,
    enabled: computed(() => unref(options.enabled) && !readsSavedBand.value),
  })

  const band = computed(() => (readsSavedBand.value ? jobBand.value : companyBand.value))

  return { band }
}
